// src/components/Raporlar.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, where, Timestamp } from 'firebase/firestore';
import { Paper, Box, Typography, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Grid, CircularProgress, Stack, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Bar, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import PrintIcon from '@mui/icons-material/Print';
// YENİ: Logo import edildi
import logo from '../assets/logo.png';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const paraFormatla = (tutar) => (tutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

// İş emirlerinde fiilen kullanılan durumlar (Faz 1 teşhisi: Teslim Edildi 295,
// Teslime Hazır 6, İşlemde 3, Beklemede 2 legacy). Eski filtre listesi "Arşiv"
// gibi hiç yazılmayan bir değer içerip, kayıtların %96'sını taşıyan
// "Teslim Edildi"yi İÇERMİYORDU — durum filtresi pratikte boş sonuç veriyordu.
const KANONIK_DURUMLAR = ['İşlemde', 'Tamamlandı', 'Teslime Hazır', 'Teslim Edildi'];

// `aracMarkaModel` tek bir string ("Land Rover Discovery"). Eski gruplama
// `split(' ')[0]` ile çok kelimeli markaları ikiye bölüyordu (Land Rover → "Land",
// Alfa Romeo → "Alfa") ve büyük/küçük harf farkını ayrı marka sayıyordu
// (gmc / GMC). Marka adı önce gerçek `vehicleBrands` listesiyle eşleştirilir.
const markaBul = (markaModel, markaAdlari) => {
  const metin = (markaModel || '').trim();
  if (!metin) return 'Bilinmiyor';
  const kucuk = metin.toLocaleLowerCase('tr-TR');
  const eslesen = markaAdlari.find(ad => {
    const adKucuk = ad.toLocaleLowerCase('tr-TR');
    return kucuk === adKucuk || kucuk.startsWith(adKucuk + ' ');
  });
  if (eslesen) return eslesen;
  // Listede yoksa ilk kelimeye düş, ama yazımı normalize et ki "gmc" ile "GMC"
  // ayrı gruplara düşmesin. Kısa kelimeler (GMC, VW, MG, DS) büyük harf kalır.
  const ilk = metin.split(/\s+/)[0];
  return ilk.length <= 3
    ? ilk.toLocaleUpperCase('tr-TR')
    : ilk.charAt(0).toLocaleUpperCase('tr-TR') + ilk.slice(1).toLocaleLowerCase('tr-TR');
};

// Bir iş emrinde geçen hizmet adları — her hizmet iş emri başına BİR kez sayılır.
// Eski kod yalnız camFilmi/ppf/yikama/diger'e bakıyordu: paketler, seramik ve
// göçük hiç sayılmıyordu (7 kategoriden 4'ü). Ayrıca cam filmi/PPF parça bazında
// sayıldığı için 6 camlı tek araç grafikte 6 işlem gibi görünüyordu.
const isEmrindekiHizmetler = (order) => {
  const adlar = new Set();
  const u = order.uygulamalar;
  if (u) {
    u.paketler?.forEach(p => { if (p?.paketAdi) adlar.add(p.paketAdi); });
    u.camFilmi?.forEach(p => { if (p?.urunAdi) adlar.add(p.urunAdi); });
    u.ppf?.forEach(p => { if (p?.urunAdi) adlar.add(p.urunAdi); });
    u.seramik?.forEach(p => { if (p?.urunAdi) adlar.add(p.urunAdi); });
    if (u.gocuk?.aktif) adlar.add('Göçük Düzeltme');
    if (u.yikama?.aktif) adlar.add('Yıkama');
    u.diger?.forEach(h => { if (h?.aciklama) adlar.add(h.aciklama); });
  } else if (order.hizmetler) {
    order.hizmetler.forEach(s => { if (s?.hizmetAdi) adlar.add(s.hizmetAdi); });
  }
  return adlar;
};

// GÜNCELLENDİ: StatCard artık yazdırırken gizlenmiyor (no-print kaldırıldı), 
// ancak yazdırırken gölgesi (elevation) kalksın diye stil eklendi.
const StatCard = ({ title, value, color = "inherit" }) => (
  <Paper
    sx={{
      p: 2,
      textAlign: 'center',
      height: '100%',
      '@media print': { border: '1px solid #ddd', boxShadow: 'none' }
    }}
  >
    <Typography color="text.secondary">{title}</Typography>
    <Typography variant="h5" fontWeight="bold" sx={{ color }}>{value}</Typography>
  </Paper>
);

const Raporlar = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [markaAdlari, setMarkaAdlari] = useState([]);
  const [hata, setHata] = useState('');

  // Marka listesi (çok kelimeli marka gruplaması için) — bir kez okunur.
  useEffect(() => {
    getDocs(query(collection(db, "vehicleBrands"), orderBy("name", "asc")))
      .then(snap => setMarkaAdlari(
        snap.docs.map(d => d.data().name).filter(Boolean)
          // Uzun ad önce denenmeli: "Land Rover", "Land"dan önce eşleşsin.
          .sort((a, b) => b.length - a.length)
      ))
      .catch(e => console.error("Marka listesi çekilemedi:", e));
  }, []);

  // Tarih aralığı SUNUCU tarafında süzülür — eskiden koleksiyonun tamamı çekilip
  // istemcide eleniyordu. Durum filtresi istemcide kalır (durum+isEmriTarihi
  // composite index'i yok ve durum kümesi küçük).
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setHata('');
      try {
        const kosullar = [];
        if (startDate) kosullar.push(where("isEmriTarihi", ">=", Timestamp.fromDate(startOfDay(startDate))));
        if (endDate) kosullar.push(where("isEmriTarihi", "<=", Timestamp.fromDate(endOfDay(endDate))));
        const q = query(collection(db, "workOrders"), ...kosullar, orderBy("isEmriTarihi", "desc"));
        const querySnapshot = await getDocs(q);
        setWorkOrders(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (e) {
        console.error("Rapor verisi çekilemedi:", e);
        setHata(e?.message || 'Rapor verisi çekilemedi.');
        setWorkOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [startDate, endDate]);

  // Filtre seçenekleri: kanonik set + veride fiilen bulunan diğer (legacy) değerler.
  const statuses = useMemo(() => {
    const veridekiler = [...new Set(workOrders.map(o => o.durum).filter(Boolean))];
    return [...KANONIK_DURUMLAR, ...veridekiler.filter(d => !KANONIK_DURUMLAR.includes(d))];
  }, [workOrders]);

  const filteredOrders = useMemo(() => {
    return workOrders.filter(order => {
      if (!order.isEmriTarihi) return false;
      if (statusFilter && order.durum !== statusFilter) return false;
      return true;
    });
  }, [workOrders, statusFilter]);

  const donemEtiketi = useMemo(() => {
    if (startDate && endDate) return `${format(startDate, 'dd MMM yyyy', { locale: tr })} – ${format(endDate, 'dd MMM yyyy', { locale: tr })}`;
    if (startDate) return `${format(startDate, 'dd MMM yyyy', { locale: tr })} sonrası`;
    if (endDate) return `${format(endDate, 'dd MMM yyyy', { locale: tr })} öncesi`;
    return 'Tüm zamanlar';
  }, [startDate, endDate]);

  const reportData = useMemo(() => {
    if (!filteredOrders.length) return null;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.toplamTutar || 0), 0);
    const totalPaid = filteredOrders.reduce((sum, order) => sum + (order.odenenTutar || 0), 0);
    const serviceCounts = {};
    const brandCounts = {};
    const aylikCiro = {};
    filteredOrders.forEach(order => {
      const brand = markaBul(order.aracMarkaModel, markaAdlari);
      brandCounts[brand] = (brandCounts[brand] || 0) + 1;

      isEmrindekiHizmetler(order).forEach(ad => { serviceCounts[ad] = (serviceCounts[ad] || 0) + 1; });

      if (order.isEmriTarihi?.toDate) {
        const ay = format(order.isEmriTarihi.toDate(), 'yyyy-MM');
        aylikCiro[ay] = (aylikCiro[ay] || 0) + (order.toplamTutar || 0);
      }
    });
    const topServices = Object.entries(serviceCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const topBrands = Object.entries(brandCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    // Son 12 ay, kronolojik sırada
    const aylikTrend = Object.entries(aylikCiro).sort((a, b) => a[0].localeCompare(b[0])).slice(-12);
    return { totalRevenue, totalPaid, topServices, topBrands, aylikTrend };
  }, [filteredOrders, markaAdlari]);

  const handleExport = () => {
    const dataToExport = filteredOrders.map(order => {
      // Excel'deki hizmet listesi de artık paketler/seramik/göçük dahil tüm
      // kategorileri kapsıyor (grafikle aynı kaynaktan).
      const hizmetlerStr = [...isEmrindekiHizmetler(order)].join(', ');
      return {
        'Plaka': order.aracPlaka || '', 'Müşteri': order.musteriAdSoyad || '', 'Telefon': order.musteriTelefon || '', 'Araç': order.aracMarkaModel || '',
        'Durum': order.durum || '', 'Tarih': order.isEmriTarihi?.toDate ? order.isEmriTarihi.toDate().toLocaleDateString('tr-TR') : '',
        'Toplam Tutar': order.toplamTutar || 0, 'Ödenen Tutar': order.odenenTutar || 0, 'Kalan Tutar': order.kalanTutar || 0,
        'Uygulamalar': hizmetlerStr
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rapor");
    XLSX.writeFile(workbook, "SolarGard_Rapor.xlsx");
  };

  const setDateFilter = (start, end) => { setStartDate(start); setEndDate(end); };
  const setToday = () => setDateFilter(startOfDay(new Date()), endOfDay(new Date()));
  const setThisWeek = () => setDateFilter(startOfWeek(new Date(), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 }));
  const setThisMonth = () => setDateFilter(startOfMonth(new Date()), endOfMonth(new Date()));
  const setLastMonth = () => { const lastMonth = subMonths(new Date(), 1); setDateFilter(startOfMonth(lastMonth), endOfMonth(lastMonth)); };
  const clearFilters = () => { setStartDate(null); setEndDate(null); setStatusFilter(''); };

  return (
    <Box className="printable-area">

      {/* YENİ: Yazdırma Logosu (Sadece print modunda görünür) */}
      <Box sx={{ display: 'none', '@media print': { display: 'flex', justifyContent: 'center', mb: 4 } }}>
        <img src={logo} alt="Logo" style={{ maxHeight: '80px', objectFit: 'contain' }} />
      </Box>

      {/* Filtre Alanı (Yazdırırken Gizlenir) */}
      <Paper sx={{ p: 2, mb: 3, '@media print': { display: 'none' } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} slotProps={{ textField: { fullWidth: true, size: 'small' } }} /></Grid>
          <Grid item xs={12} sm={6} md={3}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} slotProps={{ textField: { fullWidth: true, size: 'small' } }} /></Grid>
          <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Durum</InputLabel><Select value={statusFilter} label="Durum" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value=""><em>Tümü</em></MenuItem>{statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={3}><Button fullWidth variant="outlined" onClick={clearFilters}>Filtreleri Temizle</Button></Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 1 }}>
              <Button size="small" variant="contained" color="secondary" onClick={setToday}>Bugün</Button>
              <Button size="small" variant="contained" color="secondary" onClick={setThisWeek}>Bu Hafta</Button>
              <Button size="small" variant="contained" color="secondary" onClick={setThisMonth}>Bu Ay</Button>
              <Button size="small" variant="contained" color="secondary" onClick={setLastMonth}>Geçen Ay</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Başlık ve Butonlar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">Raporlar</Typography>
          <Typography variant="body2" color="text.secondary">
            Dönem: {donemEtiketi}{statusFilter ? ` · Durum: ${statusFilter}` : ''} · Tutarlar KDV hariç
          </Typography>
        </Box>
        <Box sx={{ '@media print': { display: 'none' } }}>
          <Button variant="contained" color="success" onClick={handleExport} disabled={!filteredOrders.length} sx={{ mr: 1 }}>Excel'e Aktar</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={!filteredOrders.length}>Yazdır</Button>
        </Box>
      </Box>

      {hata && <Alert severity="error" sx={{ mb: 2 }}>{hata}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && !hata && filteredOrders.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Seçilen dönem ve filtreye uyan iş emri bulunamadı. Tarih aralığını genişletin veya
          &quot;Filtreleri Temizle&quot; ile tüm kayıtlara dönün.
        </Alert>
      )}

      {!loading && reportData && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}><StatCard title={`Toplam Ciro · ${donemEtiketi}`} value={paraFormatla(reportData.totalRevenue)} color="primary.main" /></Grid>
            <Grid item xs={12} sm={4}><StatCard title={`Toplam Tahsilat · ${donemEtiketi}`} value={paraFormatla(reportData.totalPaid)} color="success.main" /></Grid>
            <Grid item xs={12} sm={4}><StatCard title={`Toplam Alacak · ${donemEtiketi}`} value={paraFormatla(reportData.totalRevenue - reportData.totalPaid)} color="error.main" /></Grid>
          </Grid>

          {/* Grafikler (Yazdırırken Gizlenir) */}
          <Grid container spacing={3} sx={{ mb: 3, '@media print': { display: 'none' } }}>
            <Grid item xs={12} md={6}><Paper sx={{ p: 2, height: 350 }}><Typography variant="h6">En Çok Tercih Edilen Hizmetler</Typography><Typography variant="caption" color="text.secondary">Her hizmet iş emri başına bir kez sayılır</Typography><Box sx={{ height: 'calc(100% - 50px)' }}><Bar data={{ labels: reportData.topServices.map(s => s[0]), datasets: [{ label: 'İş Emri Sayısı', data: reportData.topServices.map(s => s[1]), backgroundColor: 'rgba(53, 162, 235, 0.5)' }] }} options={{ maintainAspectRatio: false, indexAxis: 'y' }} /></Box></Paper></Grid>
            <Grid item xs={12} md={6}><Paper sx={{ p: 2, height: 350 }}><Typography variant="h6">En Çok İşlem Gören Markalar</Typography><Typography variant="caption" color="text.secondary">Marka adları araç marka listesiyle eşleştirilir</Typography><Box sx={{ height: 'calc(100% - 50px)' }}><Pie data={{ labels: reportData.topBrands.map(b => b[0]), datasets: [{ label: 'Araç Sayısı', data: reportData.topBrands.map(b => b[1]), backgroundColor: ['#2563eb', '#ef4444', '#f59e0b', '#10b981', '#64748b'] }] }} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} /></Box></Paper></Grid>

            {reportData.aylikTrend.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, height: 320 }}>
                  <Typography variant="h6">Aylık Ciro Trendi</Typography>
                  <Typography variant="caption" color="text.secondary">Seçili dönemdeki son {reportData.aylikTrend.length} ay · KDV hariç</Typography>
                  <Box sx={{ height: 'calc(100% - 50px)' }}>
                    <Bar
                      data={{
                        labels: reportData.aylikTrend.map(([ay]) => format(new Date(`${ay}-01T00:00:00`), 'MMM yyyy', { locale: tr })),
                        datasets: [{ label: 'Ciro', data: reportData.aylikTrend.map(([, tutar]) => tutar), backgroundColor: 'rgba(16, 185, 129, 0.55)' }]
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { tooltip: { callbacks: { label: (ctx) => paraFormatla(ctx.parsed.y) } } },
                        scales: { y: { ticks: { callback: (v) => (v || 0).toLocaleString('tr-TR') } } }
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Tablo Alanı */}
      <Paper sx={{ p: 2, '@media print': { boxShadow: 'none', p: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filtrelenen İş Emirleri ({filteredOrders.length})</Typography>
        </Box>

        {/* EKRAN TABLOSU (Yazdırırken Gizlenir) */}
        <TableContainer sx={{ '@media print': { display: 'none' } }}>
          <Table>
            <TableHead><TableRow><TableCell>Plaka</TableCell><TableCell>Müşteri</TableCell><TableCell>Durum</TableCell><TableCell>Tarih</TableCell><TableCell align="right">Tutar</TableCell></TableRow></TableHead>
            <TableBody>
              {filteredOrders.map(order => (
                <TableRow hover key={order.id} onClick={() => navigate(`/is-emri/${order.id}`)} sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
                  <TableCell>{order.aracPlaka}</TableCell>
                  <TableCell>{order.musteriAdSoyad}</TableCell>
                  <TableCell>{order.durum}</TableCell>
                  <TableCell>{order.isEmriTarihi?.toDate ? order.isEmriTarihi.toDate().toLocaleDateString('tr-TR') : '-'}</TableCell>
                  <TableCell align="right">{paraFormatla(order.toplamTutar)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* YAZDIRMA TABLOSU (Sadece Yazdırırken Görünür) */}
        <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plaka</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Müşteri</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tarih</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Toplam</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ödenen</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Kalan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map(order => (
                  <TableRow key={order.id} sx={{ '& td': { borderBottom: '1px solid #eee' } }}>
                    <TableCell>{order.aracPlaka}</TableCell>
                    <TableCell>{order.musteriAdSoyad}</TableCell>
                    <TableCell>{order.isEmriTarihi?.toDate ? order.isEmriTarihi.toDate().toLocaleDateString('tr-TR') : '-'}</TableCell>
                    <TableCell align="right">{paraFormatla(order.toplamTutar)}</TableCell>
                    <TableCell align="right">{paraFormatla(order.odenenTutar)}</TableCell>
                    <TableCell align="right">{paraFormatla(order.kalanTutar)}</TableCell>
                  </TableRow>
                ))}
                {reportData && (
                  <TableRow sx={{ borderTop: '2px solid black', '& td': { fontWeight: 'bold', fontSize: '1rem', pt: 2 } }}>
                    <TableCell colSpan={3} align="right">GENEL TOPLAM:</TableCell>
                    <TableCell align="right">{paraFormatla(reportData.totalRevenue)}</TableCell>
                    <TableCell align="right">{paraFormatla(reportData.totalPaid)}</TableCell>
                    <TableCell align="right">{paraFormatla(reportData.totalRevenue - reportData.totalPaid)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

      </Paper>
    </Box>
  );
};
export default Raporlar;
